import axiosInstance from "./axios";
import {
  CreatePermissionsBody,
  RevokePermissionsBody,
} from "./models/permission.models";
import {
  User,
  UserCreateBody,
  Users,
  UserUpdateBody,
} from "./models/user.models";

const apiUrl = import.meta.env.VITE_BACKEND_API_URL;

export const fetchUser = async (
  bearerToken,
  payload: { userId: number; companyId: number },
): Promise<User> => {
  try {
    const response = await axiosInstance.get(
      `${apiUrl}/Users/${payload.companyId}/${payload.userId}`,
      {
        headers: {
          Authorization: `Bearer ${bearerToken}`,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Failed to fetch company:", error);
    throw error;
  }
};

export const fetchAllSystemUsers = async (
  bearerToken: string,
  options?: {
    skip?: number;
    take?: number;
    name?: string;
    email?: string;
    companyId?: number;
    companyCode?: string;
  },
): Promise<{ data: Users[]; _count: number }> => {
  try {
    const url = new URL(`${apiUrl}/Admin/users`);

    if (options?.skip !== undefined && options?.take !== undefined) {
      url.searchParams.append("skip", options.skip.toString());
    }

    if (options?.take) {
      url.searchParams.append("take", options.take.toString());
    }

    if (options?.name) {
      url.searchParams.append("name", options.name);
    }

    if (options?.email) {
      url.searchParams.append("email", options.email);
    }

    if (options?.companyId) {
      url.searchParams.append("companyId", options.companyId.toString());
    }

    if (options?.companyCode) {
      url.searchParams.append("companyCode", options.companyCode);
    }

    const response = await axiosInstance.get(url.toString(), {
      headers: {
        Authorization: `Bearer ${bearerToken}`,
      },
    });

    // Extract total count from response header
    const totalHeader =
      response.headers["total"] || response.headers["x-total-count"];
    const total = totalHeader
      ? parseInt(totalHeader, 10)
      : response.data.length;

    return {
      data: response.data,
      _count: total,
    };
  } catch (error) {
    console.error("Failed to fetch all system users:", error);
    throw error;
  }
};

export const fetchAllCompanyUsers = async (
  bearerToken,
  payload: {
    companyId: number;
    organizationId?: number;
    email?: string;
    name?: string;
  },
): Promise<Users[]> => {
  try {
    const url = new URL(`${apiUrl}/Users/${payload.companyId}/all`);
    if (payload.organizationId) {
      url.searchParams.append(
        "organizationId",
        payload.organizationId.toString(),
      );
    }

    if (payload.email) {
      url.searchParams.append("email", payload.email);
    }

    if (payload.name) {
      url.searchParams.append("name", payload.name);
    }

    const response = await axiosInstance.get(url.toString(), {
      headers: {
        Authorization: `Bearer ${bearerToken}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Failed to fetch company:", error);
    throw error;
  }
};

export const createCompanyUser = async (
  bearerToken: string,
  payload: UserCreateBody & { companyId: number; organizationId?: number },
): Promise<User> => {
  try {
    const response = await axiosInstance.post(
      `${apiUrl}/Users/${payload.companyId}${payload.organizationId ? `?organizationId=${payload.organizationId}` : ""}`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${bearerToken}`,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Failed to create company:", error);
    throw error;
  }
};

export const updateCompanyUser = async (
  bearerToken: string,
  payload: UserUpdateBody & { companyId: number; organizationId?: number },
): Promise<User> => {
  try {
    const response = await axiosInstance.put(
      `${apiUrl}/Users/${payload.companyId}${payload.organizationId ? `?organizationId=${payload.organizationId}` : ""}`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${bearerToken}`,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Failed to update user:", error);
    throw error;
  }
};

export const deleteCompanyUser = async (
  bearerToken: string,
  payload: { companyId: number; userId: number; organizationId?: number },
): Promise<boolean> => {
  try {
    const response = await axiosInstance.delete(
      `${apiUrl}/Users/${payload.companyId}/${payload.userId}${payload.organizationId ? `?organizationId=${payload.organizationId}` : ""}`,
      {
        headers: {
          Authorization: `Bearer ${bearerToken}`,
        },
      },
    );
    return response.status === 200;
  } catch (error) {
    console.error("Failed to delete user:", error);
    throw error;
  }
};

export const adminCreateUser = async (
  bearerToken: string,
  payload: UserCreateBody,
): Promise<User> => {
  try {
    const response = await axiosInstance.post(
      `${apiUrl}/Admin/users`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${bearerToken}`,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Failed to create company:", error);
    throw error;
  }
};

export const adminUpdateUser = async (
  bearerToken: string,
  payload: UserUpdateBody,
): Promise<User> => {
  try {
    const response = await axiosInstance.put(`${apiUrl}/Admin/users`, payload, {
      headers: {
        Authorization: `Bearer ${bearerToken}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Failed to update user:", error);
    throw error;
  }
};

export const adminDeleteUser = async (
  bearerToken: string,
  userId: number,
): Promise<boolean> => {
  try {
    const response = await axiosInstance.delete(
      `${apiUrl}/Admin/users/${userId}`,
      {
        headers: {
          Authorization: `Bearer ${bearerToken}`,
        },
      },
    );
    return response.status === 200;
  } catch (error) {
    console.error("Failed to delete user:", error);
    throw error;
  }
};

export const sendUserPasswordResetEmail = async (
  bearerToken: string,
  payload: {
    companyId: number;
    userId: number;
  },
): Promise<boolean> => {
  try {
    const response = await axiosInstance.post(
      `${apiUrl}/Users/${payload.companyId}/sendpasschange/${payload.userId}`,
      null,
      {
        headers: {
          Authorization: `Bearer ${bearerToken}`,
        },
      },
    );
    return response.status === 200;
  } catch (error) {
    console.error("Failed to send password reset email:", error);
    throw error;
  }
};

export const adminSendUserInviteEmail = async (
  bearerToken: string,
  payload: {
    companyId: number;
    userId: number;
    organizationId?: number;
  },
): Promise<boolean> => {
  try {
    const response = await axiosInstance.post(
      `${apiUrl}/Admin/users/${payload.companyId}/senduserinvite/${payload.userId}${payload.organizationId ? `?organizationId=${payload.organizationId}` : ""}`,
      null,
      {
        headers: {
          Authorization: `Bearer ${bearerToken}`,
        },
      },
    );
    return response.status === 200;
  } catch (error) {
    console.error("Failed to send user invite email:", error);
    throw error;
  }
};

export const sendUserInviteEmail = async (
  bearerToken: string,
  payload: {
    companyId: number;
    userId: number;
    organizationId?: number;
  },
): Promise<boolean> => {
  try {
    const response = await axiosInstance.post(
      `${apiUrl}/Users/${payload.companyId}/senduserinvite/${payload.userId}${payload.organizationId ? `?organizationId=${payload.organizationId}` : ""}`,
      null,
      {
        headers: {
          Authorization: `Bearer ${bearerToken}`,
        },
      },
    );
    return response.status === 200;
  } catch (error) {
    console.error("Failed to send user invite email:", error);
    throw error;
  }
};

export const adminGrantUserPermissions = async (
  bearerToken: string,
  payload: CreatePermissionsBody,
): Promise<any> => {
  try {
    const response = await axiosInstance.post(
      `${apiUrl}/Admin/users/grantPermissions${payload.organizationId ? `?organizationId=${payload.organizationId}` : ""}`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${bearerToken}`,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Failed to update user permissions:", error);
    throw error;
  }
};

export const adminRevokeUserPermissions = async (
  bearerToken: string,
  payload: RevokePermissionsBody,
): Promise<any> => {
  try {
    const response = await axiosInstance.post(
      `${apiUrl}/Admin/users/revokePermissions${payload.organizationId ? `?organizationId=${payload.organizationId}` : ""}`,
      {
        ...payload,
      },
      {
        headers: {
          Authorization: `Bearer ${bearerToken}`,
        },
      },
    );
    return response.status === 200;
  } catch (error) {
    console.error("Failed to revoke user permissions:", error);
    throw error;
  }
};

export const grantUserPermission = async (
  bearerToken: string,
  payload: CreatePermissionsBody,
  companyId: number,
): Promise<any> => {
  try {
    const response = await axiosInstance.post(
      `${apiUrl}/Users/${companyId}/grantPermissions${payload.organizationId ? `?organizationId=${payload.organizationId}` : ""}`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${bearerToken}`,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Failed to update user permissions:", error);
    throw error;
  }
};

export const revokeUserPermission = async (
  bearerToken: string,
  payload: RevokePermissionsBody,
  companyId: number,
): Promise<any> => {
  try {
    const response = await axiosInstance.post(
      `${apiUrl}/Users/${companyId}/revokePermissions${payload.organizationId ? `?organizationId=${payload.organizationId}` : ""}`,
      {
        ...payload,
      },
      {
        headers: {
          Authorization: `Bearer ${bearerToken}`,
        },
      },
    );
    return response.status === 200;
  } catch (error) {
    console.error("Failed to revoke user permissions:", error);
    throw error;
  }
};

// Fetch admin users from /api/v1/Admin/admins
export const fetchAdminUsers = async (
  bearerToken: string,
): Promise<Users[]> => {
  try {
    const response = await axiosInstance.get(`${apiUrl}/Admin/admins`, {
      headers: {
        Authorization: `Bearer ${bearerToken}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Failed to fetch admin users:", error);
    throw error;
  }
};
